// Libraries
import { useMemo } from 'react';
// Components
import { Label } from '@atoms/index';
import { BasicOption, Dropdown, FixedDropdown } from '@molecules/index';
import { ColumnFilterProps, FilterVariant } from '@organisms/index';
// Types
import { DropdownFilterProps } from './DropdownFilter.types';

/**
 * DropdownFilter component for selecting a year from a dropdown menu.
 * @component
 *
 * @template TableData - The type of data in the table.
 * @param {ColumnFilterProps<TableData, RatingsButtonsFilterProps>} props - The component props.
 * @param {Column<TableData, unknown>} props.column - The column filter props.
 * @param {RatingsButtonsFilterProps} props.props - The additional props specific to the ratings buttons filter.
 * @param {FilterVariant} props.variant - Represent the visibility state of a filter depending on the view breakpoint.
 *
 * @returns {ReactElement} - The React element representing the YearPicker component.
 */
export const DropdownFilter = <TableData,>({
  column,
  props,
  variant,
}: ColumnFilterProps<TableData, DropdownFilterProps>) => {
  // Destructure properties and methods from props
  const { id, name, isMulti, label, placeholder } = props;
  // Destructure properties and methods from 'column' prop
  const { getFilterValue, setFilterValue, options } = column;
  const w = variant === FilterVariant.VISIBLE ? 50 : '';

  // Get current filter values
  const values = (getFilterValue() as []) ?? [];
  const initialValues = values.map((value) => ({ value, label: value }));
  const selectedLabel = values.join(', ');

  // Generate options for the dropdown using memoization
  const initialOptions = useMemo(
    () =>
      options.sort().map((value) => ({
        value: String(value),
        label: String(value),
      })),
    [options]
  );

  /**
   * Event handler for applying the selected options.
   *
   * @param {Option} selectedOptions - The selected option containing the picked values.
   */
  const handleOnChange = (selectedOptions: BasicOption | BasicOption[]) => {
    if (Array.isArray(selectedOptions)) {
      const selectedValues = selectedOptions.map(({ value }) => value);
      if (selectedOptions.length > 0) {
        setFilterValue([...new Set([...selectedValues])]);
      } else {
        setFilterValue(undefined);
      }
    } else {
      setFilterValue(selectedOptions);
    }
  };

  return variant === FilterVariant.HIDDEN ? (
    <FixedDropdown
      addSelectAll
      isMulti
      id={id}
      name={name}
      selectedLabel={selectedLabel}
      options={initialOptions}
      value={initialValues}
      onChange={handleOnChange}
      placeholder={placeholder}
    />
  ) : (
    <Label label={label}>
      <Dropdown
        {...props}
        isMulti={isMulti}
        options={initialOptions}
        value={initialValues}
        onChange={handleOnChange}
        w={w}
      />
    </Label>
  );
};
